import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const AdvancedOperationsManager = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('transporters');
  
  // Transporters state
  const [transporters, setTransporters] = useState([]);
  const [showTransporterModal, setShowTransporterModal] = useState(false);
  const [editingTransporter, setEditingTransporter] = useState(null);
  const [transporterForm, setTransporterForm] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    specialties: [],
    notes: ''
  });

  // Payment terms state
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [showPaymentTermsModal, setShowPaymentTermsModal] = useState(false);
  const [editingPaymentTerms, setEditingPaymentTerms] = useState(null);
  const [paymentTermsForm, setPaymentTermsForm] = useState({
    name: '',
    description: '',
    payment_delay_days: 30,
    discount_percentage: 0,
    discount_days: 0,
    is_default: false
  });

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    document_type: 'devis',
    is_default: false
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const transportSpecialties = ['maritime', 'routier', 'aérien', 'ferroviaire'];
  const documentTypes = ['devis', 'bon_commande', 'facture'];

  useEffect(() => {
    fetchTransporters();
    fetchPaymentTerms();
    fetchEmailTemplates();
  }, []);

  // Fetch functions
  const fetchTransporters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/transporters`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransporters(data);
      }
    } catch (error) {
      console.error('Error fetching transporters:', error);
    }
  };

  const fetchPaymentTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/payment-terms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentTerms(data);
      }
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/email-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  // Transporter functions
  const handleCreateTransporter = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/transporters`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transporterForm)
      });
      
      if (response.ok) {
        await fetchTransporters();
        closeTransporterModal();
        alert('Transporteur créé avec succès !');
      } else {
        alert('Erreur lors de la création du transporteur');
      }
    } catch (error) {
      console.error('Error creating transporter:', error);
      alert('Erreur lors de la création du transporteur');
    }
  };

  const handleUpdateTransporter = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/transporters/${editingTransporter.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transporterForm)
      });
      
      if (response.ok) {
        await fetchTransporters();
        closeTransporterModal();
        alert('Transporteur mis à jour avec succès !');
      } else {
        alert('Erreur lors de la mise à jour du transporteur');
      }
    } catch (error) {
      console.error('Error updating transporter:', error);
      alert('Erreur lors de la mise à jour du transporteur');
    }
  };

  const handleDeleteTransporter = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir désactiver ce transporteur ?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/transporters/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          await fetchTransporters();
          alert('Transporteur désactivé avec succès !');
        } else {
          alert('Erreur lors de la désactivation du transporteur');
        }
      } catch (error) {
        console.error('Error deleting transporter:', error);
        alert('Erreur lors de la désactivation du transporteur');
      }
    }
  };

  // Payment terms functions
  const handleCreatePaymentTerms = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/payment-terms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentTermsForm)
      });
      
      if (response.ok) {
        await fetchPaymentTerms();
        closePaymentTermsModal();
        alert('Conditions de paiement créées avec succès !');
      } else {
        alert('Erreur lors de la création des conditions de paiement');
      }
    } catch (error) {
      console.error('Error creating payment terms:', error);
      alert('Erreur lors de la création des conditions de paiement');
    }
  };

  const handleUpdatePaymentTerms = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/payment-terms/${editingPaymentTerms.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentTermsForm)
      });
      
      if (response.ok) {
        await fetchPaymentTerms();
        closePaymentTermsModal();
        alert('Conditions de paiement mises à jour avec succès !');
      } else {
        alert('Erreur lors de la mise à jour des conditions de paiement');
      }
    } catch (error) {
      console.error('Error updating payment terms:', error);
      alert('Erreur lors de la mise à jour des conditions de paiement');
    }
  };

  const handleDeletePaymentTerms = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ces conditions de paiement ?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/payment-terms/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          await fetchPaymentTerms();
          alert('Conditions de paiement supprimées avec succès !');
        } else {
          alert('Erreur lors de la suppression des conditions de paiement');
        }
      } catch (error) {
        console.error('Error deleting payment terms:', error);
        alert('Erreur lors de la suppression des conditions de paiement');
      }
    }
  };

  // Email template functions
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/email-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });
      
      if (response.ok) {
        await fetchEmailTemplates();
        closeTemplateModal();
        alert('Template email créé avec succès !');
      } else {
        alert('Erreur lors de la création du template email');
      }
    } catch (error) {
      console.error('Error creating email template:', error);
      alert('Erreur lors de la création du template email');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/email-templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });
      
      if (response.ok) {
        await fetchEmailTemplates();
        closeTemplateModal();
        alert('Template email mis à jour avec succès !');
      } else {
        alert('Erreur lors de la mise à jour du template email');
      }
    } catch (error) {
      console.error('Error updating email template:', error);
      alert('Erreur lors de la mise à jour du template email');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce template email ?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/email-templates/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          await fetchEmailTemplates();
          alert('Template email supprimé avec succès !');
        } else {
          alert('Erreur lors de la suppression du template email');
        }
      } catch (error) {
        console.error('Error deleting email template:', error);
        alert('Erreur lors de la suppression du template email');
      }
    }
  };

  // Modal functions
  const openTransporterModal = (transporter = null) => {
    if (transporter) {
      setEditingTransporter(transporter);
      setTransporterForm({
        name: transporter.name,
        contact_person: transporter.contact_person,
        email: transporter.email || '',
        phone: transporter.phone || '',
        specialties: transporter.specialties || [],
        notes: transporter.notes || ''
      });
    }
    setShowTransporterModal(true);
  };

  const closeTransporterModal = () => {
    setShowTransporterModal(false);
    setEditingTransporter(null);
    setTransporterForm({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      specialties: [],
      notes: ''
    });
  };

  const openPaymentTermsModal = (terms = null) => {
    if (terms) {
      setEditingPaymentTerms(terms);
      setPaymentTermsForm({
        name: terms.name,
        description: terms.description,
        payment_delay_days: terms.payment_delay_days,
        discount_percentage: terms.discount_percentage,
        discount_days: terms.discount_days,
        is_default: terms.is_default
      });
    }
    setShowPaymentTermsModal(true);
  };

  const closePaymentTermsModal = () => {
    setShowPaymentTermsModal(false);
    setEditingPaymentTerms(null);
    setPaymentTermsForm({
      name: '',
      description: '',
      payment_delay_days: 30,
      discount_percentage: 0,
      discount_days: 0,
      is_default: false
    });
  };

  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        subject: template.subject,
        body: template.body,
        document_type: template.document_type,
        is_default: template.is_default
      });
    }
    setShowTemplateModal(true);
  };

  const closeTemplateModal = () => {
    setShowTemplateModal(false);
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      subject: '',
      body: '',
      document_type: 'devis',
      is_default: false
    });
  };

  const handleSpecialtyChange = (specialty) => {
    const updatedSpecialties = transporterForm.specialties.includes(specialty)
      ? transporterForm.specialties.filter(s => s !== specialty)
      : [...transporterForm.specialties, specialty];
    
    setTransporterForm({...transporterForm, specialties: updatedSpecialties});
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-baobab-primary to-baobab-dark text-white p-6 rounded-lg">
        <h2 className="text-2xl font-bold mb-2">Gestion Avancée des Opérations 🚛</h2>
        <p className="text-baobab-light">Configuration des transporteurs, conditions de paiement et templates email</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {[
              { id: 'transporters', name: 'Transporteurs', icon: '🚛' },
              { id: 'payment-terms', name: 'Conditions de Paiement', icon: '💳' },
              { id: 'email-templates', name: 'Templates Email', icon: '📧' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-baobab-primary text-baobab-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Transporters Tab */}
          {activeTab === 'transporters' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Gestion des Transporteurs</h3>
                <button
                  onClick={() => openTransporterModal()}
                  className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors flex items-center space-x-2"
                >
                  <span>+</span>
                  <span>Nouveau Transporteur</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {transporters.map((transporter) => (
                  <div key={transporter.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{transporter.name}</h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openTransporterModal(transporter)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteTransporter(transporter.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Contact: {transporter.contact_person}</p>
                    {transporter.email && (
                      <p className="text-sm text-gray-600 mb-2">Email: {transporter.email}</p>
                    )}
                    {transporter.phone && (
                      <p className="text-sm text-gray-600 mb-2">Tél: {transporter.phone}</p>
                    )}
                    {transporter.specialties && transporter.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {transporter.specialties.map((specialty) => (
                          <span
                            key={specialty}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {transporters.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucun transporteur configuré. Cliquez sur "Nouveau Transporteur" pour commencer.
                </div>
              )}
            </div>
          )}

          {/* Payment Terms Tab */}
          {activeTab === 'payment-terms' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Conditions de Paiement</h3>
                <button
                  onClick={() => openPaymentTermsModal()}
                  className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors flex items-center space-x-2"
                >
                  <span>+</span>
                  <span>Nouvelles Conditions</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paymentTerms.map((terms) => (
                  <div key={terms.id} className={`p-4 rounded-lg border ${terms.is_default ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                        <span>{terms.name}</span>
                        {terms.is_default && <span className="text-green-600 text-sm">(Par défaut)</span>}
                      </h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openPaymentTermsModal(terms)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeletePaymentTerms(terms.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{terms.description}</p>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600">Délai: {terms.payment_delay_days} jours</p>
                      {terms.discount_percentage > 0 && (
                        <p className="text-sm text-gray-600">
                          Remise: {terms.discount_percentage}% si paiement sous {terms.discount_days} jours
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {paymentTerms.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucune condition de paiement configurée. Cliquez sur "Nouvelles Conditions" pour commencer.
                </div>
              )}
            </div>
          )}

          {/* Email Templates Tab */}
          {activeTab === 'email-templates' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Templates Email</h3>
                <button
                  onClick={() => openTemplateModal()}
                  className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors flex items-center space-x-2"
                >
                  <span>+</span>
                  <span>Nouveau Template</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {emailTemplates.map((template) => (
                  <div key={template.id} className={`p-4 rounded-lg border ${template.is_default ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                        <span>{template.name}</span>
                        {template.is_default && <span className="text-green-600 text-sm">(Par défaut)</span>}
                      </h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openTemplateModal(template)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Type: {template.document_type}</p>
                    <p className="text-sm text-gray-600 mb-2">Sujet: {template.subject}</p>
                    <p className="text-sm text-gray-500 truncate">{template.body.substring(0, 100)}...</p>
                  </div>
                ))}
              </div>

              {emailTemplates.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucun template email configuré. Cliquez sur "Nouveau Template" pour commencer.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Transporter Modal */}
      {showTransporterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingTransporter ? 'Modifier le transporteur' : 'Nouveau transporteur'}
              </h3>
              <button
                onClick={closeTransporterModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={editingTransporter ? handleUpdateTransporter : handleCreateTransporter} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom du transporteur</label>
                <input
                  type="text"
                  value={transporterForm.name}
                  onChange={(e) => setTransporterForm({...transporterForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Personne de contact</label>
                <input
                  type="text"
                  value={transporterForm.contact_person}
                  onChange={(e) => setTransporterForm({...transporterForm, contact_person: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={transporterForm.email}
                  onChange={(e) => setTransporterForm({...transporterForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                <input
                  type="tel"
                  value={transporterForm.phone}
                  onChange={(e) => setTransporterForm({...transporterForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Spécialités</label>
                <div className="space-y-2">
                  {transportSpecialties.map((specialty) => (
                    <label key={specialty} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={transporterForm.specialties.includes(specialty)}
                        onChange={() => handleSpecialtyChange(specialty)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 capitalize">{specialty}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={transporterForm.notes}
                  onChange={(e) => setTransporterForm({...transporterForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeTransporterModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-baobab-primary text-white rounded-lg hover:bg-baobab-dark"
                >
                  {editingTransporter ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Terms Modal */}
      {showPaymentTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingPaymentTerms ? 'Modifier les conditions' : 'Nouvelles conditions de paiement'}
              </h3>
              <button
                onClick={closePaymentTermsModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={editingPaymentTerms ? handleUpdatePaymentTerms : handleCreatePaymentTerms} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                <input
                  type="text"
                  value={paymentTermsForm.name}
                  onChange={(e) => setPaymentTermsForm({...paymentTermsForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={paymentTermsForm.description}
                  onChange={(e) => setPaymentTermsForm({...paymentTermsForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  rows={3}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Délai de paiement (jours)</label>
                <input
                  type="number"
                  value={paymentTermsForm.payment_delay_days}
                  onChange={(e) => setPaymentTermsForm({...paymentTermsForm, payment_delay_days: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  required
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remise (%)</label>
                <input
                  type="number"
                  value={paymentTermsForm.discount_percentage}
                  onChange={(e) => setPaymentTermsForm({...paymentTermsForm, discount_percentage: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Jours pour la remise</label>
                <input
                  type="number"
                  value={paymentTermsForm.discount_days}
                  onChange={(e) => setPaymentTermsForm({...paymentTermsForm, discount_days: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  min="0"
                />
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={paymentTermsForm.is_default}
                    onChange={(e) => setPaymentTermsForm({...paymentTermsForm, is_default: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Conditions par défaut</span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closePaymentTermsModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-baobab-primary text-white rounded-lg hover:bg-baobab-dark"
                >
                  {editingPaymentTerms ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingTemplate ? 'Modifier le template' : 'Nouveau template email'}
              </h3>
              <button
                onClick={closeTemplateModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom du template</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de document</label>
                <select
                  value={templateForm.document_type}
                  onChange={(e) => setTemplateForm({...templateForm, document_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  required
                >
                  {documentTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sujet</label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Corps du message</label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-baobab-primary"
                  rows={8}
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Placeholders disponibles: {'{document_reference}'}, {'{client_name}'}, {'{total_ttc}'}
                </p>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={templateForm.is_default}
                    onChange={(e) => setTemplateForm({...templateForm, is_default: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Template par défaut pour ce type de document</span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeTemplateModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-baobab-primary text-white rounded-lg hover:bg-baobab-dark"
                >
                  {editingTemplate ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedOperationsManager;